// 3rd party imports
import * as React from 'react';
import { Link } from 'react-router-dom';

// shared-ui
import { ILocalization } from '@shared-ui/localization-context';

// mobx
import { Program } from 'models';
import { Features } from 'models/program';

// ui
import { UitkText } from 'uitk-react-text';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkSpacing } from 'uitk-react-spacing';

export interface ProgramCardFeaturesProps {
  program: Program;
  l10n?: ILocalization;
  layout: string;
}

export const ProgramCardFeatures: React.FunctionComponent<ProgramCardFeaturesProps> = ({ program, l10n, layout }) => {
  function tooltipCode(dialogId) {
    return (
      <Link to={{ search: `pwaDialog=${dialogId}-${program.name}` }}>
        <UitkIcon name="info_outline" size={UitkIconSize.SMALL} />
      </Link>
    );
  }

  function buildL10nArguments(featureName) {
    switch (featureName) {
      case 'commission':
        return [program.commissionPercentage];
      case 'cookies':
        return [program.cookieDurationDays];
    }

    return {};
  }

  function getFeatureLabel(feature: Features) {
    return l10n.formatText(`programs.cards.features.${feature.name}.label`);
  }

  function getFeatureValue(feature: Features, dialogId) {
    if (feature.name === 'commission') {
      if (feature.hasTooltip) {
        return (
          <Link to={{ search: `pwaDialog=${dialogId}-${program.name}` }}>
            <UitkLayoutFlex alignItems="center">
              <UitkText weight="medium" inline={true}>
                {l10n.formatText(`programs.cards.features.${feature.name}.value`, buildL10nArguments(feature.name))}
              </UitkText>
              <UitkIcon
                name="info_outline"
                size={UitkIconSize.SMALL}
                className="ProgramCard__feature--dialog-trigger"
              />
            </UitkLayoutFlex>
          </Link>
        );
      }

      return (
        <UitkText weight="medium">
          {program.name === 'us-bex'
            ? l10n.formatText(`programs.cards.features.us-bex.${feature.name}.value`, buildL10nArguments(feature.name))
            : l10n.formatText(`programs.cards.features.${feature.name}.value`, buildL10nArguments(feature.name))}
        </UitkText>
      );
    }

    return (
      <UitkText>
        {l10n.formatText(`programs.cards.features.${feature.name}.value`, buildL10nArguments(feature.name))}
      </UitkText>
    );
  }

  const isCard = layout === 'card';

  return (
    <UitkLayoutFlex
      className="ProgramCard__features"
      direction={isCard ? 'column' : 'row'}
      alignItems={isCard ? 'stretch' : 'start'}
    >
      {program.features.map((feature, index) => (
        <UitkSpacing padding={isCard ? { blockend: 'one' } : { inlineend: 'six' }} key={`feature-${index}`}>
          <UitkLayoutFlexItem grow={1} maxWidth="full_width">
            <UitkLayoutFlex direction={isCard ? 'row' : 'column'} justifyContent={isCard ? 'space-between' : 'start'}>
              <UitkLayoutFlexItem>
                <UitkText size={200}>{getFeatureLabel(feature)}</UitkText>
              </UitkLayoutFlexItem>
              <UitkLayoutFlexItem>{getFeatureValue(feature, feature.tooltipDialogPrefix)}</UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </UitkLayoutFlexItem>
        </UitkSpacing>
      ))}
    </UitkLayoutFlex>
  );
};
